'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Form, FormControl, FormItem } from '@ahafood/ui/components/form';
import { Image } from '@ahafood/ui/components/image';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@ahafood/ui/components/sheet';
import { Textarea } from '@ahafood/ui/components/textarea';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Star, X } from 'lucide-react';
import React, { useEffect, useState, memo, useMemo } from 'react';
import { useForm, useController, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { RatingImageUpload } from './rating-image-upload';
import { useAddComment } from '@/api/use-add-comment';
import { useDeleteRatingImage } from '@/api/use-delete-rating-image';
import { useSubmitRating } from '@/api/use-submit-rating';
import { useUploadRatingImage } from '@/api/use-upload-rating-image';
import { getAuthToken } from '@/lib/auth/get-token';
import { useIsMobile } from '@/lib/is-mobile';

const formSchema = z.object({
  rating: z.number().min(1, 'Rating is required'),
  reasons: z.array(z.string()).default([]),
  comment: z.string().default(''),
});

type RatingFormData = z.infer<typeof formSchema>;

interface RatingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitRating?: () => void; // Optional callback when rating is successfully submitted
  onShowSuccessDialog?: () => void; // Optional callback to show success dialog
  orderDetail: {
    code: string;
    store_name: string;
    store_image_url?: string;
  };
  initialStep?: 1 | 2; // Optional: which step to start on
  existingRating?: number; // Optional: existing rating for comment-only mode
  hideImageUpload?: boolean; // Optional: hide image upload component
  initialData?: {
    comment?: string;
    reasons?: string[];
    images?: string[];
  }; // Optional: initial data for editing
}

const RATING_REASONS_MAP = {
  5: [
    {
      id: 'taste_delicious',
      label: 'Hương vị ngon',
      icon: 'rating-good-2.webp',
    },
    {
      id: 'portion_generous',
      label: 'Khẩu phần nhiều',
      icon: 'rating-good-4.webp',
    },
    {
      id: 'package_perfect',
      label: 'Đóng gói hoàn hảo',
      icon: 'rating-good-1.webp',
    },
    {
      id: 'delivery_complete',
      label: 'Giao đủ món',
      icon: 'rating-good-5.webp',
    },
    {
      id: 'image_match',
      label: 'Món ăn giống hình',
      icon: 'rating-good-3.webp',
    },
    {
      id: 'hygiene_safe',
      label: 'Vệ sinh an toàn',
      icon: 'rating-good-6.webp',
    },
  ],
  4: [
    { id: 'taste_good', label: 'Hương vị ổn', icon: 'rating-good-2.webp' },
    {
      id: 'portion_adequate',
      label: 'Khẩu phần đủ dùng',
      icon: 'rating-good-4.webp',
    },
    {
      id: 'package_neat',
      label: 'Đóng gói gọn gàng',
      icon: 'rating-good-1.webp',
    },
    { id: 'delivery_full', label: 'Giao đủ món', icon: 'rating-good-5.webp' },
    {
      id: 'image_close',
      label: 'Món ăn gần giống hình',
      icon: 'rating-good-3.webp',
    },
    {
      id: 'hygiene_good',
      label: 'Vệ sinh đảm bảo',
      icon: 'rating-good-6.webp',
    },
  ],
  3: [
    {
      id: 'taste_unsuitable',
      label: 'Hương vị chưa hợp',
      icon: 'rating-bad-1.webp',
    },
    {
      id: 'portion_less',
      label: 'Khẩu phần hơi ít',
      icon: 'rating-bad-3.webp',
    },
    {
      id: 'package_rough',
      label: 'Đóng gói sơ sài',
      icon: 'rating-bad-4.webp',
    },
    {
      id: 'delivery_wrong',
      label: 'Giao sai/thiếu món',
      icon: 'rating-bad-6.webp',
    },
    {
      id: 'image_different',
      label: 'Món ăn khác hình',
      icon: 'rating-bad-2.webp',
    },
    {
      id: 'hygiene_poor',
      label: 'Vệ sinh không đảm bảo',
      icon: 'rating-bad-5.webp',
    },
  ],
  2: [
    {
      id: 'taste_bad',
      label: 'Hương vị không ngon',
      icon: 'rating-bad-1.webp',
    },
    { id: 'portion_little', label: 'Khẩu phần ít', icon: 'rating-bad-3.webp' },
    {
      id: 'package_sloppy',
      label: 'Đóng gói sơ sài',
      icon: 'rating-bad-4.webp',
    },
    {
      id: 'delivery_incorrect',
      label: 'Giao sai/thiếu món',
      icon: 'rating-bad-6.webp',
    },
    {
      id: 'image_very_diff',
      label: 'Món ăn rất khác hình',
      icon: 'rating-bad-2.webp',
    },
    {
      id: 'hygiene_unsafe',
      label: 'Vệ sinh không đảm bảo',
      icon: 'rating-bad-5.webp',
    },
  ],
  1: [
    {
      id: 'taste_terrible',
      label: 'Hương vị không ngon',
      icon: 'rating-bad-1.webp',
    },
    {
      id: 'portion_very_small',
      label: 'Khẩu phần rất ít',
      icon: 'rating-bad-3.webp',
    },
    {
      id: 'package_broken',
      label: 'Đóng gói bị hỏng',
      icon: 'rating-bad-4.webp',
    },
    {
      id: 'delivery_missing',
      label: 'Giao sai/thiếu món',
      icon: 'rating-bad-6.webp',
    },
    {
      id: 'image_totally_diff',
      label: 'Món ăn khác xa mong đợi',
      icon: 'rating-bad-2.webp',
    },
    {
      id: 'hygiene_terrible',
      label: 'Vệ sinh không đảm bảo',
      icon: 'rating-bad-5.webp',
    },
  ],
};

// --- Rating Step ---
const RatingStep = memo(function RatingStep({
  currentRating,
  hoverRating: _hoverRating,
  displayRating,
  isSubmitting,
  orderDetail,
  selectedReasons,
  onStarClick,
  onStarHover,
  onStarLeave,
  toggleReason,
}: {
  currentRating: number;
  hoverRating: number;
  displayRating: number;
  isSubmitting: boolean;
  orderDetail: RatingModalProps['orderDetail'];
  selectedReasons: string[];
  onStarClick: (r: number) => void;
  onStarHover: (r: number) => void;
  onStarLeave: () => void;
  toggleReason: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center p-6 space-y-4 flex-1',
        currentRating === 0 && 'justify-center h-full pb-40'
      )}
    >
      <div className="flex flex-col items-center space-y-1">
        <div className="size-20 rounded-full flex items-center justify-center">
          <Image
            src={orderDetail.store_image_url ?? ''}
            alt={orderDetail.store_name}
            width={64}
            height={64}
            className="size-20 rounded-full object-cover"
          />
        </div>
        <p className="text-sm text-neutral-60">{orderDetail.store_name}</p>
      </div>

      <div className="text-center max-w-[70%]">
        <h2 className="text-xl font-bold text-neutral-90">
          Bạn đánh giá thế nào về đơn hàng này?
        </h2>
      </div>

      <div className="flex justify-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            type="button"
            key={star}
            onClick={() => onStarClick(star)}
            onMouseEnter={() => onStarHover(star)}
            onMouseLeave={onStarLeave}
            className="p-1 transition-colors"
            disabled={isSubmitting}
          >
            <Star
              className={cn(
                'w-10 h-10 transition-colors',
                star <= displayRating
                  ? 'fill-primary-40 text-primary-40'
                  : 'text-neutral-30 fill-neutral-30'
              )}
            />
          </button>
        ))}
      </div>

      {currentRating > 0 && (
        <div className="w-full mt-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
          <p className="text-center text-neutral-70 font-medium mb-4">
            {currentRating === 5 || currentRating === 4
              ? 'Bạn ấn tượng về điều gì?'
              : 'Quán cần cải thiện điều gì'}
          </p>

          <div className="grid grid-cols-3 gap-3">
            {(
              RATING_REASONS_MAP[
                currentRating as keyof typeof RATING_REASONS_MAP
              ] || []
            ).map((reason) => (
              <button
                type="button"
                key={reason.id}
                onClick={() => toggleReason(reason.id)}
                disabled={isSubmitting}
                className={cn(
                  'flex flex-col items-center p-4 rounded-xl border transition-colors',
                  selectedReasons.includes(reason.id)
                    ? 'border-primary-40 bg-primary-10'
                    : 'bg-white'
                )}
              >
                <div className="mb-2 flex items-center justify-center rounded-full">
                  <Image
                    src={`${assetPrefix}/static/images/${reason.icon}`}
                    alt={reason.label}
                    width={62}
                    height={62}
                    className="object-contain"
                  />
                </div>
                <span className="text-xs text-center leading-tight">
                  {reason.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// --- Comment Step ---
const CommentStep = memo(function CommentStep({
  form,
  currentRating,
  isSubmitting,
  orderDetail,
  onFilesChange,
  hideImageUpload = false,
  isEditing = false,
  existingImages = [],
  onDeleteExistingImage,
}: {
  form: ReturnType<typeof useForm<RatingFormData>>;
  currentRating: number;
  isSubmitting: boolean;
  orderDetail: RatingModalProps['orderDetail'];
  onFilesChange: (files: File[]) => void;
  hideImageUpload?: boolean;
  isEditing?: boolean;
  existingImages?: string[];
  onDeleteExistingImage?: (imageUrl: string) => void;
}) {
  const {
    field: commentField,
    fieldState: { error },
  } = useController({ name: 'comment', control: form.control });

  const RatingStars = memo(function RatingStars({
    rating,
  }: {
    rating: number;
  }) {
    return (
      <div className="flex justify-center space-x-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              'w-5 h-5',
              star <= rating
                ? 'fill-primary-40 text-primary-40'
                : 'text-neutral-30 fill-neutral-30'
            )}
          />
        ))}
      </div>
    );
  });

  const ReasonLabels = memo(function ReasonLabels({
    reasons,
    rating,
  }: {
    reasons: string[];
    rating: number;
  }) {
    const reasonLabels = useMemo(() => {
      const map =
        RATING_REASONS_MAP[rating as keyof typeof RATING_REASONS_MAP] || [];
      return reasons
        .map((id) => map.find((r) => r.id === id)?.label ?? id)
        .join(' • ');
    }, [reasons, rating]);

    if (reasons.length === 0) return null;

    return (
      <p className="text-sm text-neutral-60 font-light">
        Cụ thể hơn về nhận xét{' '}
        <span className="font-bold text-neutral-90">{reasonLabels}</span>
      </p>
    );
  });

  const reasons = useWatch({ control: form.control, name: 'reasons' });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 p-6">
        <div className="text-left mb-6">
          <div className="flex items-center w-full gap-4">
            <div className="size-10 rounded-full flex items-center justify-center mb-4">
              <Image
                src={
                  orderDetail.store_image_url ??
                  `${assetPrefix}/static/images/empty-store-bot.webp`
                }
                alt={orderDetail.store_name}
                width={64}
                height={64}
                className="size-10 rounded-full object-cover"
              />
            </div>
            <div className="flex flex-col items-start">
              <p className="text-xs text-neutral-60 mb-1">
                {orderDetail.store_name}
              </p>
              <RatingStars rating={currentRating} />
            </div>
          </div>

          <h2 className="text-lg font-bold text-neutral-90 mb-2">
            Cảm nhận của bạn về món ăn
          </h2>

          <ReasonLabels reasons={reasons} rating={currentRating} />
        </div>

        <div className="space-y-4">
          <FormItem>
            <FormControl>
              <Textarea
                {...commentField}
                placeholder="Hãy chia sẻ cảm nhận chi tiết để giúp nhà hàng phục vụ tốt hơn..."
                className="min-h-[100px] resize-none border-neutral-20 transition-colors placeholder:text-xs"
                disabled={isSubmitting}
                maxLength={2000}
              />
            </FormControl>
            <p className="text-xs text-neutral-40 mt-1">
              {commentField.value?.length || 0}/2000 ký tự
            </p>
            {error && (
              <span className="text-xs text-red-50">{error.message}</span>
            )}
          </FormItem>

          {(!hideImageUpload || isEditing) && (
            <RatingImageUpload
              disabled={isSubmitting}
              onFilesChange={onFilesChange}
              existingImages={existingImages}
              onDeleteExistingImage={onDeleteExistingImage}
            />
          )}
        </div>
      </div>
    </div>
  );
});

// --- Main Component ---
// eslint-disable-next-line sonarjs/cognitive-complexity
export function RatingModal({
  open,
  onOpenChange,
  onSubmitRating: _onSubmitRating,
  onShowSuccessDialog,
  orderDetail,
  initialStep = 1,
  existingRating,
  hideImageUpload = false,
  initialData,
}: RatingModalProps) {
  const token = getAuthToken();
  const isMobile = useIsMobile();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [hoverRating, setHoverRating] = useState(0);
  const [currentRating, setCurrentRating] = useState(existingRating ?? 0);
  const [selectedReasons, setSelectedReasons] = useState<string[]>(
    initialData?.reasons ?? []
  );
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>(
    initialData?.images ?? []
  );
  const [hasCompletedStep1, setHasCompletedStep1] = useState(false);

  // Track if we're in editing mode
  const isEditingMode = Boolean(initialData);

  // Original values for comparison in edit mode
  const originalValues = useMemo(
    () => ({
      comment: initialData?.comment ?? '',
      reasons: initialData?.reasons ?? [],
      images: initialData?.images ?? [],
    }),
    [initialData]
  );

  // API hooks
  const queryClient = useQueryClient();
  const submitRatingMutation = useSubmitRating(token);
  const addCommentMutation = useAddComment(token);
  const uploadImageMutation = useUploadRatingImage(token);
  const deleteRatingImageMutation = useDeleteRatingImage(token);

  // Calculate loading states
  const isSubmitting =
    submitRatingMutation.isPending ||
    addCommentMutation.isPending ||
    uploadImageMutation.isPending ||
    deleteRatingImageMutation.isPending;

  const form = useForm<RatingFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      rating: existingRating ?? 0,
      reasons: initialData?.reasons ?? [],
      comment: initialData?.comment ?? '',
    },
  });

  // Watch the comment field for changes
  const commentValue = useWatch({
    control: form.control,
    name: 'comment',
    defaultValue: initialData?.comment ?? '',
  });

  // Check if there are any changes in edit mode
  const hasChanges = useMemo(() => {
    if (!isEditingMode) return true; // Always allow submit in create mode

    const currentComment = (commentValue ?? '').trim();
    const currentReasons = Array.from(selectedReasons);
    const currentExistingImages = existingImages;

    // Compare comment
    if (currentComment !== originalValues.comment.trim()) {
      return true;
    }

    // Compare reasons (order doesn't matter)
    if (currentReasons.length !== originalValues.reasons.length) {
      return true;
    }

    if (
      !currentReasons.every((reason) => originalValues.reasons.includes(reason))
    ) {
      return true;
    }

    // Compare existing images (check if any were deleted)
    if (currentExistingImages.length !== originalValues.images.length) {
      return true;
    }

    // Compare existing images by ID
    if (
      !currentExistingImages.every((img) => originalValues.images.includes(img))
    ) {
      return true;
    }

    // Check if new images were added
    if (uploadedImages.length > 0) {
      return true;
    }

    return false;
  }, [
    isEditingMode,
    commentValue,
    selectedReasons,
    existingImages,
    uploadedImages,
    originalValues,
  ]);

  const handleDeleteExistingImage = async (imageUrl: string) => {
    try {
      await deleteRatingImageMutation.mutateAsync({
        order_code: orderDetail.code,
        image_url: imageUrl,
      });

      // Remove the image from existing images
      setExistingImages((prev) => prev.filter((img) => img !== imageUrl));

      // Invalidate store ratings queries to refetch new data
      await queryClient.invalidateQueries({
        queryKey: ['store-ratings'],
      });

      toast.success('Đã xóa ảnh thành công!');
    } catch (error) {
      toast.error('Có lỗi xảy ra khi xóa ảnh');
      console.error('Failed to delete rating image:', error);
    }
  };

  const handleStarClick = (rating: number) => {
    form.setValue('rating', rating);
    setCurrentRating(rating);
    // Clear selected reasons when rating changes
    setSelectedReasons([]);
    form.setValue('reasons', []);
  };

  const toggleReason = (reasonId: string) => {
    const newReasons = selectedReasons.includes(reasonId)
      ? selectedReasons.filter((id) => id !== reasonId)
      : [...selectedReasons, reasonId];
    form.setValue('reasons', newReasons);
    setSelectedReasons(newReasons);
  };

  const submitStep1 = async (data: RatingFormData) => {
    try {
      await submitRatingMutation.mutateAsync({
        order_code: orderDetail.code,
        overall: data.rating,
        tags: data.reasons,
      });

      // Invalidate store ratings queries to refetch new data
      await queryClient.invalidateQueries({
        queryKey: ['store-ratings'],
      });

      setHasCompletedStep1(true);
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to submit rating:', error);
    }
  };

  const submitStep2 = async (data: RatingFormData) => {
    const hasComment = data.comment.trim().length > 0;
    const hasImages = uploadedImages.length > 0;
    const commentChanged = isEditingMode
      ? data.comment.trim() !== originalValues.comment
      : hasComment;

    if (!commentChanged && !hasImages) {
      handleClose();
      return;
    }

    try {
      // Only call addComment if comment has changed (in edit mode) or if it's new (create mode)
      if (commentChanged) {
        await addCommentMutation.mutateAsync({
          order_code: orderDetail.code,
          comment: data.comment,
        });
      }

      if (hasImages) {
        await Promise.all(
          uploadedImages.map((image) =>
            uploadImageMutation.mutateAsync({
              order_code: orderDetail.code,
              image,
            })
          )
        );
      }

      // Invalidate store ratings queries to refetch new data
      await queryClient.invalidateQueries({
        queryKey: ['store-ratings'],
      });

      // Show different feedback based on mode
      if (isEditingMode) {
        // Show toast for edit mode
        toast.success('Đã cập nhật nhận xét thành công!', {
          description:
            'Bạn có thể cập nhật lại nhận xét trong vòng 7 ngày kể từ khi đơn hoàn thành.',
        });
      } else {
        // Show success dialog for create mode
        onShowSuccessDialog?.();
      }

      _onSubmitRating?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit comment/images:', error);
    }
  };

  const onSubmit = async (data: RatingFormData) => {
    if (currentStep === 1 && data.rating > 0) {
      await submitStep1(data);
    } else if (currentStep === 2) {
      await submitStep2(data);
    }
  };

  const handleClose = () => {
    // Show success dialog if user completed step 1 and not in edit mode
    if (hasCompletedStep1 && !isEditingMode) {
      onShowSuccessDialog?.();
    }

    onOpenChange(false);
    setCurrentStep(initialStep);
    setHoverRating(0);
    setCurrentRating(existingRating ?? 0);
    setSelectedReasons(initialData?.reasons ?? []);
    setUploadedImages([]);
    setExistingImages(initialData?.images ?? []);
    setHasCompletedStep1(false);
    form.reset();
  };

  // Reset form when modal opens - do this outside useEffect
  useEffect(() => {
    if (open) {
      setCurrentStep(initialStep);
      setHoverRating(0);
      setCurrentRating(existingRating ?? 0);
      setSelectedReasons(initialData?.reasons ?? []);
      setUploadedImages([]);
      setExistingImages(initialData?.images ?? []);
      setHasCompletedStep1(false);
    }
  }, [open, initialStep, existingRating, initialData]);

  // Reset form separately to avoid infinite loops
  useEffect(() => {
    if (open) {
      form.reset({
        rating: existingRating ?? 0,
        reasons: initialData?.reasons ?? [],
        comment: initialData?.comment ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingRating, initialData]); // Intentionally exclude form

  const displayRating = hoverRating || currentRating;

  const RatingContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="h-full flex flex-col"
      >
        <div className="flex-1 h-[calc(100dvh-73px)] overflow-y-auto">
          {currentStep === 1 ? (
            <RatingStep
              currentRating={currentRating}
              hoverRating={hoverRating}
              displayRating={displayRating}
              isSubmitting={isSubmitting}
              orderDetail={orderDetail}
              selectedReasons={selectedReasons}
              onStarClick={handleStarClick}
              onStarHover={setHoverRating}
              onStarLeave={() => setHoverRating(0)}
              toggleReason={toggleReason}
            />
          ) : (
            <CommentStep
              form={form}
              currentRating={currentRating}
              isSubmitting={isSubmitting}
              orderDetail={orderDetail}
              onFilesChange={setUploadedImages}
              hideImageUpload={hideImageUpload}
              existingImages={existingImages}
              isEditing={Boolean(initialData)}
              onDeleteExistingImage={handleDeleteExistingImage}
            />
          )}
        </div>

        <div className="py-4 px-6 pb-[calc(env(safe-area-inset-bottom))] md:pb-4 bg-white">
          <Button
            type="submit"
            className={cn(
              'w-full text-white h-12',
              currentRating === 0 && 'bg-neutral-30 cursor-not-allowed'
            )}
            disabled={currentRating === 0 || isSubmitting || !hasChanges}
          >
            {isSubmitting ? 'Đang gửi...' : 'Gửi đánh giá'}
            {/* // : currentStep === 1
              //   ? 'Gửi đánh giá'
              //   : 'Hoàn thành'} */}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent side="bottom" className="size-full p-0 overflow-hidden">
          <SheetHeader className="flex flex-row items-center justify-between px-4 py-3 border-b bg-white gap-0 space-y-0">
            <Button
              size="icon"
              // onClick={currentStep > 1 ? () => setCurrentStep(1) : handleClose}
              onClick={handleClose}
              variant="ghost"
              className="rounded-full shadow-4 size-8"
            >
              <X className="size-5" />
            </Button>
            <SheetTitle className="text-center flex-1 mt-0 h-5">
              Đánh giá đơn hàng
            </SheetTitle>
            <div className="w-8" />
          </SheetHeader>
          <div className="h-[calc(100%-73px)]">{RatingContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md mx-auto p-0 overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between px-4 py-3 border-b bg-white gap-0 space-y-0">
          <div className="w-10" />

          <DialogTitle className="text-center flex-1">
            Đánh giá đơn hàng
          </DialogTitle>
          <Button
            size="icon"
            onClick={handleClose}
            variant="ghost"
            className="rounded-full bg-white z-50"
          >
            <X className="size-5" />
          </Button>
        </DialogHeader>
        <div
          className={cn(
            'overflow-y-auto',
            currentStep === 1 && form.getValues('rating') > 0
              ? 'h-[650px]'
              : 'h-[500px]'
          )}
        >
          {RatingContent}
        </div>
      </DialogContent>
    </Dialog>
  );
}
